import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtOdds, apiError } from "../lib/api";
import { Stars, ConfBar } from "../components/Bits";
import { ShareWinCard } from "../components/ShareWinCard";
import { BallparkModal } from "../components/BallparkModal";
import { BookLinks } from "../components/BookLinks";
import { findGameForTeam, parkFitScore, BALLPARKS } from "../lib/ballparks";
import { Lock, Share2, Flame, Crosshair, Ticket } from "lucide-react";

const SECTIONS = [
  { cat: "lock", title: "Locks", sub: "5-star. Our highest conviction plays.", color: "cat-lock", card: "cat-lock-card" },
  { cat: "value", title: "Value", sub: "4-star. The market is off — take the edge.", color: "cat-value", card: "cat-value-card" },
  { cat: "longshot", title: "Long Shots", sub: "Low risk, huge payoff swings.", color: "cat-longshot", card: "cat-longshot-card" },
  { cat: "fish", title: "Fish of The Day", sub: "Moneyline · Spread · Over/Under — free for everybody.", color: "cat-fish", card: "fish-card" },
];

function ParkFitBadge({ pick, players, games, onOpen }) {
  const pl = (players || []).find((x) => x.mlb_id === pick.mlb_id);
  const game = pl ? findGameForTeam(games, pl.team_name) : null;
  const dims = game ? BALLPARKS[game.home]?.dims : null;
  const { data: spray } = useQuery({
    queryKey: ["spray", pick.mlb_id],
    queryFn: () => api.get(`/batter-spray/${pick.mlb_id}`).then((r) => r.data),
    enabled: Boolean(dims && pick.mlb_id),
    staleTime: 12 * 3600 * 1000,
  });
  if (!dims) return null;
  const fit = spray ? parkFitScore(spray, dims) : null;
  if (!fit) return null;
  const color = fit.score >= 7 ? "text-[#22c55e] border-[#22c55e]/50 bg-[#22c55e]/10" : fit.score >= 4 ? "text-[#a855f7] border-[#a855f7]/50 bg-[#a855f7]/10" : "text-rose-400 border-rose-500/50 bg-rose-500/10";
  return (
    <button onClick={() => onOpen(pick)} data-testid={`park-fit-${pick.id}`} title={`${fit.out} of ${fit.total} of their HRs clear tonight's wall — tap for the spray chart`}
      className={`font-data text-[10px] font-bold uppercase tracking-wider border rounded px-2 py-0.5 transition-colors duration-200 ${color}`}>
      Park Fit {fit.score}/10
    </button>
  );
}

const RESULT_STYLE = { win: "bg-[#22c55e]/20 text-[#22c55e]", loss: "bg-red-500/20 text-red-400", pending: "bg-white/10 text-neutral-400" };

export default function Board() {
  const navigate = useNavigate();
  const [sport, setSport] = useState("mlb");
  const { data } = useQuery({ queryKey: ["board", sport], queryFn: () => api.get(`/board?sport=${sport}`).then((r) => r.data) });
  const { data: tracker } = useQuery({ queryKey: ["tracker"], queryFn: () => api.get("/hit-tracker").then((r) => r.data) });
  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const { data: wxData } = useQuery({ queryKey: ["weather"], queryFn: () => api.get("/weather").then((r) => r.data) });
  const picks = data?.picks || [];
  const [sharePick, setSharePick] = useState(null);
  const [spray, setSpray] = useState(null);
  const [showBooks, setShowBooks] = useState(false);

  const tailMut = useMutation({
    mutationFn: () => {
      const legs = picks.slice(0, 10).map((p) => `${p.player_name} — ${p.bet} (${fmtOdds(p.odds)})`);
      const dec = picks.slice(0, 10).reduce((acc, p) => acc * (p.odds > 0 ? 1 + p.odds / 100 : 1 + 100 / Math.abs(p.odds)), 1);
      const american = dec >= 2 ? `+${Math.round((dec - 1) * 100)}` : `-${Math.round(100 / (dec - 1))}`;
      return api.post("/community/posts", {
        content: `Tailing today's board (${data?.date}) — who's riding with the bear?`,
        channel: "main", kind: "parlay", legs, odds: american,
      });
    },
    onSuccess: () => { toast.success("Board posted as a parlay slip in Community"); setShowBooks(true); },
    onError: (e) => toast.error(apiError(e)),
  });

  const openSpray = (pick) => {
    const pl = (playersData?.players || []).find((x) => x.mlb_id === pick.mlb_id);
    if (!pl) return;
    const game = findGameForTeam(wxData?.games, pl.team_name);
    if (!game) return;
    setSpray({ game, batter: pl });
  };

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="font-chalk text-5xl md:text-6xl text-white -rotate-1">Today's Board</h1>
          {tracker?.streak?.type === "W" && tracker.streak.count >= 2 && (
            <span className="inline-flex items-center gap-1.5 border border-[#a855f7]/60 bg-[#a855f7]/10 text-[#a855f7] text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full" data-testid="board-streak-badge">
              <Flame size={13} /> {tracker.streak.count} Pick Heater
            </span>
          )}
          {picks.length >= 2 && (
            <button onClick={() => tailMut.mutate()} disabled={tailMut.isPending} data-testid="tail-board-btn"
              className="btn-shine inline-flex items-center gap-1.5 text-black text-xs uppercase tracking-widest font-black px-4 py-2 rounded-full disabled:opacity-50">
              <Ticket size={13} /> {tailMut.isPending ? "Posting..." : "Tail this board"}
            </button>
          )}
        </div>
        <p className="text-neutral-500 text-sm mt-1">{data?.date} · curated daily by the Pooh Sheet team</p>
        <div className="flex gap-1.5 mt-3 overflow-x-auto" data-testid="board-sport-chips">
          {["mlb", "nfl", "nba", "nhl", "ncaaf", "ncaab"].map((s) => (
            <button key={s} onClick={() => setSport(s)} data-testid={`board-sport-${s}`}
              className={`shrink-0 text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full border transition-colors duration-200 ${sport === s ? "bg-[#a855f7] text-black border-[#a855f7]" : "border-white/15 text-neutral-500 hover:border-[#a855f7]/50 hover:text-[#a855f7]"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="chalk-texture border border-white/15 rounded-xl p-5 md:p-8 space-y-8 fade-up-1">
        {SECTIONS.map(({ cat, title, sub, color, card }) => {
          const rows = picks.filter((p) => p.category === cat);
          if (rows.length === 0 && (cat === "fish" || !data?.locked_count)) return null;
          return (
            <div key={cat}>
              <div className="flex items-baseline gap-3 border-b-2 border-dashed border-white/20 pb-2">
                <h2 className={`font-chalk text-4xl ${color}`} style={{ filter: "drop-shadow(0 0 10px currentColor)" }}>{title}</h2>
                <span className="text-xs text-neutral-400">{sub}</span>
              </div>
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                {rows.map((p) => (
                  <div key={p.id} data-testid={`pick-card-${p.id}`} className={`bg-black/40 border border-white/10 rounded-lg p-4 transition-colors duration-200 ${card}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#a855f7] font-bold"><Stars n={p.stars} /></div>
                        <div className="font-chalk text-3xl text-white leading-tight">{p.player_name}</div>
                        <div className="text-sm text-neutral-300">{p.team} · {p.bet}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-data text-xl font-bold text-[#22c55e]">{fmtOdds(p.odds)}</div>
                        <span className={`inline-block text-[10px] uppercase font-bold px-2 py-0.5 rounded mt-1 ${RESULT_STYLE[p.result] || RESULT_STYLE.pending}`}>{p.result}</span>
                      </div>
                    </div>
                    {p.note && <p className="text-xs text-neutral-400 mt-3 italic">"{p.note}"</p>}
                    <div className="mt-3"><ConfBar value={p.confidence} /></div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      {p.mlb_id ? (
                        <div className="flex items-center gap-3 flex-wrap">
                          <Link to={`/research/${p.mlb_id}`} data-testid={`pick-research-${p.id}`} className="text-[11px] uppercase tracking-wider font-bold text-[#a855f7] hover:text-white transition-colors duration-200">
                            Open research →
                          </Link>
                          <button onClick={() => openSpray(p)} data-testid={`pick-spray-${p.id}`}
                            className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-neutral-400 border border-white/15 hover:border-[#a855f7] hover:text-[#a855f7] px-2.5 py-1 rounded transition-colors duration-200">
                            <Crosshair size={11} /> Park Fit
                          </button>
                          {(p.bet || "").toLowerCase().includes("hr") && (
                            <ParkFitBadge pick={p} players={playersData?.players} games={wxData?.games} onOpen={openSpray} />
                          )}
                        </div>
                      ) : <span />}
                      {p.result === "win" && (
                        <button onClick={() => setSharePick({ ...p, date: data?.date })} data-testid={`share-win-${p.id}`}
                          className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#22c55e] border border-[#22c55e]/40 hover:bg-[#22c55e]/10 px-2.5 py-1 rounded transition-colors duration-200">
                          <Share2 size={11} /> Share the W
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {data?.locked_count > 0 && (
          <div className="border-2 border-dashed border-[#a855f7]/40 rounded-lg p-8 text-center" data-testid="board-locked-banner">
            <Lock className="mx-auto text-[#a855f7] mb-2" size={24} />
            <div className="font-chalk text-3xl text-white">{data.locked_count} more picks are chalked up behind the paywall</div>
            <p className="text-neutral-400 text-sm mt-1">Pro members see the full board — every Lock, Value play and Long Shot.</p>
            <Link to="/settings" data-testid="board-upgrade-cta" className="inline-block mt-4 bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase tracking-wider text-sm px-6 py-2.5 rounded transition-colors duration-200">
              Upgrade to Pro
            </Link>
          </div>
        )}
      </div>
      {sharePick && <ShareWinCard pick={sharePick} onClose={() => setSharePick(null)} />}
      {spray && <BallparkModal game={spray.game} initialBatter={spray.batter} onClose={() => setSpray(null)} />}
      {showBooks && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowBooks(false)} data-testid="tail-books-modal">
          <div className="card-gold border border-[#a855f7]/40 rounded-xl p-5 max-w-md w-full gold-glow-strong" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-black uppercase gold-text">Board Tailed!</h3>
            <p className="text-sm text-neutral-400 mt-1 mb-4">Your slip is live in Community. Now place it at the book you bet with:</p>
            <BookLinks title="Choose your sportsbook" testid="tail-book-links" />
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setShowBooks(false); navigate("/community"); }} data-testid="tail-view-community"
                className="flex-1 bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase text-xs tracking-wider py-2.5 rounded transition-colors duration-200">
                View in Community
              </button>
              <button onClick={() => setShowBooks(false)} data-testid="tail-books-close"
                className="border border-white/20 text-neutral-300 font-bold uppercase text-xs tracking-wider px-4 rounded hover:border-white/40 transition-colors duration-200">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
